import { AxiosResponse } from 'axios';
import axiosInstance from '../../services/axios/axios';
import { WrapImsPacket } from '../../store/utility';
import { then, catchAxiosError } from '../../services/axios/_utility';

/**
 * Asynchronously retrieves end-user's notifications.
 * @param {Number} id end-user unique identifier.
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise<AxiosResponse<object[]>>} array of end-user notification objects.
 */
export const getNotifications = async (id, dispatch = null) => {
	try {
		return axiosInstance
			.get(`api/Notification/${id}`)
			.then(response => then(response, payload => payload, dispatch));
	} catch (e) {
		throw catchAxiosError(e);
	}
};

/**
 * Asynchronously retrieves expiring non-employees within purview of end-user.
 * @param {Object} payload request payload.
 * @param {Number} payload.Id end-user unique identifier.
 * @param {Date} [payload.ExpireBy] expiration scope of consideration.
 * @param {Boolean} [payload.IncludeExpiring=true] toggle whether to restrict scope of consideration to 30 days; overrides `ExpireBy`.
 * @param {Number} [payload.Range=-1] number of expiring non-employees to retrieve.
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise<AxiosResponse<object[]>>} array of end-user expiring non-employee objects.
 */
export const getExpiringNonEmployees = async (payload, dispatch = null) => {
	try {
		return axiosInstance
			.post(
				`api/ESO/NonEmployee/GetExpiringNonEmployees`,
				WrapImsPacket(payload)
			)
			.then(response => then(response, payload => payload, dispatch));
	} catch (e) {
		throw catchAxiosError(e);
	}
};

/**
 * Asynchronously retrieves work queue requests assigned to end-user.
 * @param {Object} payload request payload.
 * @param {Number} payload.ID end-user unique identifier.
 * @param {Boolean} [payload.Pending=true] toggle whether to include requests pending some event action.
 * @param {string} [payload.TrackingNumber] unique identifier of request of interest.
 * @param {Number} [payload.AppId=-1] unique identifier of application of interest.
 * @param {string} [payload.RequesteeFirst] first name of requestee of interest.
 * @param {string} [payload.RequesteeLast] last name of requestee of interest.
 * @param {string} [payload.RequesteeNetworkID] network ID of requestee of interest.
 * @param {string} [payload.RequestorNetworkID] network ID of requestor of interest.
 * @param {Number} [payload.Range=-1] number of work queue requests to retrieve.
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise<AxiosResponse<object[]>>} array of end-user work queue objects.
 */
export const getWorkQueueRequests = async (payload, dispatch = null) => {
	try {
		return axiosInstance
			.post(`api/WorkQueue/GetRequests`, WrapImsPacket(payload))
			.then(response => then(response, payload => payload, dispatch));
	} catch (e) {
		throw catchAxiosError(e);
	}
};

export const getSearchItems = async (roles, dispatch = null) => {
	try {
		return axiosInstance
			.post(`api/Menu/Search`, roles)
			.then(response => then(response, payload => payload, dispatch));
	} catch (ex) {
		throw catchAxiosError(ex);
	}
};

//Menu API call
export const getMenuItems = async (esoID, dispatch) => {
	return await axiosInstance
		.get(`api/Menu/getMenuItems?esoID=${esoID}`)
		.then(response => then(response, payload => payload, dispatch))
		.catch(ex => {
			console.error(
				'error when querying menu items from API for ESO id:',
				esoID
			);
		});
};

// Update Fav API call
export const updateMenuFavorites = async (esoID, updatedFavorites) => {
	try {
		const response = await axiosInstance.put(`api/Menu/updateFavorites`, {
			esoID: esoID,
			favorites: updatedFavorites,
		});
		return response.data;
	} catch (ex) {
		console.error(ex);
		return {
			data: null,
			error: `unable to update favorites for current user`,
		};
	}
};

// Get Fav call
export const getMenuFavorites = async esoID => {
	try {
		const response = await axiosInstance.get(
			`api/Menu/getFavorites?esoID=${esoID}`
		);
		return response.data;
	} catch (ex) {
		console.error(`unable to fetch favorites related to ESO id:${esoID}`);
		return {
			data: null,
			error: `unable to fetch favorites related to ESO id:${esoID}`,
		};
	}
};
